import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Editor, Element, Transforms, createEditor } from "slate";
import { Slate, Editable, withReact } from "slate-react";
import { withHistory } from "slate-history";
import { withLists, ListType, withListsReact } from "@prezly/slate-lists";
import CodeElement from "./CodeElement";
import DefaultElement from "./DefaultElement";
import Leaf from "./Leaf";
import styled from "styled-components";
import { serialize } from "./TextSerializer";
import ExpandableToolbar from "../ExpandableToolbar";
import {
  FormatBoldRounded,
  FormatItalicRounded,
  FormatListBulletedRounded,
  FormatStrikethroughRounded,
  LinkRounded,
  RedoRounded,
  UndoRounded,
} from "@mui/icons-material";
import { t } from "i18next";
import { htmlToPlainText } from "../Modal/AddFeedbackModalCustomerType";
const initialValue = [
  {
    type: "paragraph",
    children: [{ text: "" }],
  },
];
const EditorContainer = styled.div`
  border: 1px solid #ccc;
  border-radius: 4px;
  // padding: 1rem;
  margin: 0 auto;
  width: -webkit-fill-available;
  min-height: 10vh;
`;
const StyledEditable = styled(Editable)`
  border: none;
  font-family: "Inter", sans-serif;
  padding: 1.2rem;
  background-color: azure;
  line-height: 1.5;
  border-radius: 8px;
  height: 25vh;
  overflow: scroll;
  scroll-behavior: smooth;
  scrollbar-width: none;
`;

// const Type = {
//     PARAGRAPH: 'paragraph',
//     ORDERED_LIST: 'ordered-list',
//     UNORDERED_LIST: 'unordered-list',
//     LIST_ITEM: 'list-item',
//     LIST_ITEM_TEXT: 'list-item-text',
// }

// const withListsPlugin = withLists({
//     isConvertibleToListTextNode(node) {
//         return Element.isElementType(node, Type.PARAGRAPH);
//     },
//     isDefaultTextNode(node) {
//         return Element.isElementType(node, Type.PARAGRAPH);
//     },
//     isListNode(node, type) {
//         if (type) {
//             const nodeType =
//                 type === ListType.ORDERED_LIST ? Type.ORDERED_LIST : Type.UNORDERED_LIST;
//             return Element.isElementType(node, nodeType);
//         }
//         return (
//             Element.isElementType(node, Type.ORDERED_LIST) ||
//             Element.isElementType(node, Type.UNORDERED_LIST)
//         );
//     },
//     isListItemNode(node) {
//         return Element.isElementType(node, Type.LIST_ITEM);
//     },
//     isListItemTextNode(node) {
//         return Element.isElementType(node, Type.LIST_ITEM_TEXT);
//     },
//     createDefaultTextNode(props = {}) {
//         return { children: [{ text: '' }], ...props, type: Type.PARAGRAPH };
//     },
//     createListNode(type = ListType.UNORDERED, props = {}) {
//         const nodeType = type === ListType.ORDERED_LIST ? Type.ORDERED_LIST : Type.UNORDERED_LIST;
//         return { children: [{ text: '' }], ...props, type: nodeType };
//     },
//     createListItemNode(props = {}) {
//         return { children: [{ text: '' }], ...props, type: Type.LIST_ITEM };
//     },
//     createListItemTextNode(props = {}) {
//         return { children: [{ text: '' }], ...props, type: Type.LIST_ITEM_TEXT };
//     },
// });

const CustomEditorActions = {
  isBoldMarkActive(editor) {
    const marks = Editor.marks(editor);
    return marks ? marks.bold === true : false;
  },

  isCodeBlockActive(editor) {
    const [match] = Editor.nodes(editor, {
      match: (n) => n.type === "code",
    });

    return !!match;
  },
  isItalicMarkActive(editor) {
    const marks = Editor.marks(editor);
    return marks ? marks.italic === true : false;
  },
  isStrikeThroughMarkActive(editor) {
    const marks = Editor.marks(editor);
    return marks ? marks.strikethrough === true : false;
  },
  // isListActive(editor, format) {
  //     const [match] = Editor.nodes(editor, {
  //         match: n =>
  //             n.type === format,
  //     });

  //     return !!match;
  // },
  isLinkActive(editor) {
    const [link] = Editor.nodes(editor, { match: (n) => n.type === "link" });
    return !!link;
  },

  toggleBoldMark(editor) {
    const isActive = CustomEditorActions.isBoldMarkActive(editor);
    if (isActive) {
      Editor.removeMark(editor, "bold");
    } else {
      Editor.addMark(editor, "bold", true);
    }
    return true;
  },

  toggleCodeBlock(editor) {
    const isActive = CustomEditorActions.isCodeBlockActive(editor);
    Transforms.setNodes(
      editor,
      { type: isActive ? null : "code" },
      { match: (n) => Editor.isBlock(editor, n) }
    );
    return true;
  },
  toggleItalicMark(editor) {
    const isActive = CustomEditorActions.isItalicMarkActive(editor);
    if (isActive) {
      Editor.removeMark(editor, "italic");
    } else {
      Editor.addMark(editor, "italic", true);
    }
    return true;
  },
  toggleStrikeThroughMark(editor) {
    const isActive = CustomEditorActions.isStrikeThroughMarkActive(editor);
    if (isActive) {
      Editor.removeMark(editor, "strikethrough");
    } else {
      Editor.addMark(editor, "strikethrough", true);
    }
    return true;
  },
  undo(editor) {
    editor.undo();
    return true;
  },
  redo(editor) {
    editor.redo();
    return true;
  },
  // toggleBulletList(editor) {
  //     const isActive = CustomEditorActions.isListActive(editor, Type.UNORDERED_LIST)
  //     Transforms.unwrapNodes(editor, { match: n => n.type === Type.UNORDERED_LIST, split: true })
  //     if (!isActive) {
  //         const listItem = { type: Type.LIST_ITEM, children: [] }
  //         Transforms.wrapNodes(editor, listItem, { match: n => Editor.isBlock(editor, n) })
  //         const list = { type: Type.UNORDERED_LIST, children: [] }
  //         Transforms.wrapNodes(editor, list, { match: n => n.type === Type.LIST_ITEM })
  //     }
  // }
  addLink(editor, url) {
    if (!url) return;
    const { selection } = editor;
    const isLinkActive = CustomEditorActions.isLinkActive(editor);

    if (isLinkActive) {
      Transforms.unwrapNodes(editor, { match: (n) => n.type === "link" });
    }

    const [parentNode] = Editor.parent(editor, selection.focus.path);
    if (parentNode.type === "link") return;

    const link = { type: "link", url, children: [] };
    Transforms.wrapNodes(editor, link, { split: true });
    Transforms.collapse(editor, { edge: "end" });
  },
};

const editorTools = [
  {
    clickHandler: CustomEditorActions.toggleBoldMark,
    icon: FormatBoldRounded,
  },
  {
    clickHandler: CustomEditorActions.toggleItalicMark,
    icon: FormatItalicRounded,
  },
  {
    clickHandler: CustomEditorActions.toggleStrikeThroughMark,
    icon: FormatStrikethroughRounded,
  },
  {
    clickHandler: CustomEditorActions.undo,
    icon: UndoRounded,
  },
  {
    clickHandler: CustomEditorActions.redo,
    icon: RedoRounded,
  },
  // {
  //     'clickHandler': CustomEditorActions.toggleBulletList,
  //     'icon': FormatListBulletedRounded
  // },
  {
    clickHandler: (editor) => {
      const url = window.prompt(t("general.label.enter.link"));
      CustomEditorActions.addLink(editor, url);
    },
    icon: LinkRounded,
  },
];
const RichTextEditor = (props) => {
  // const [editor, _] = useState(useMemo(() => withListsReact(withListsPlugin(withReact(withHistory(createEditor()))))))
  const [editor, _] = useState(
    useMemo(() => withReact(withHistory(createEditor())), [])
  );
  const [value, setValue] = useState(initialValue);
  const renderElement = useCallback((props) => {
    switch (props.element.type) {
      case "code":
        return <CodeElement {...props} />;
      // case 'dropdown':
      //     return <DropdownElement {...props} />
      case "link":
        return (
          <a
            style={{ textDecoration: "underline" }}
            {...props.attributes}
            href={props.element.url}
          >
            {props.children}
          </a>
        ); //LinkElement
      case "bulleted-list":
        return <ul {...props.attributes}>{props.children}</ul>; //BulletListElement
      case "numbered-list":
        return <ol {...props.attributes}>{props.children}</ol>; //NumberListElement
      case "list-item":
        return <li {...props.attributes}>{props.children}</li>; //ListItemElement
      default:
        return <DefaultElement {...props} />;
    }
  });
  const renderLeaf = useCallback((props) => {
    return <Leaf {...props} />;
  });
  useEffect(() => {}, [props.r]);
  const saveContent = (val) => {
    const content = editor.children;
    var html = content.map((node) => serialize(node)).join("\n");
    html = html.replaceAll("\n", "<br />").replaceAll("\r", "<br />");
    sessionStorage.setItem("editorContent", html);
    window.dispatchEvent(new Event("storage"));
  };
  useEffect(() => {
    window.addEventListener("storageEditExternal", (event) => {
      props.updateEditor(event, editor);
    });
    return () => {
      window.removeEventListener("storageEditExternal", () => {
        console.log("removed");
      });
    };
  }, []);
  return (
    <EditorContainer className="custom-input-border">
      <ExpandableToolbar
        editor={editor}
        tools={editorTools}
      ></ExpandableToolbar>
      <Slate
        editor={editor}
        initialValue={initialValue}
        onValueChange={(val) => saveContent(val)}
      >
        <StyledEditable
          className="border-focus-none py-2"
          value={value}
          // placeholder="Enter some rich text…"
          renderElement={renderElement}
          renderLeaf={renderLeaf}
          onKeyDown={(event) => {
            // saveContent();
            if (event.key === "Enter") {
              event.preventDefault();
              editor.insertText("\n");
            }
            if (!event.ctrlKey) {
              // saveContent();
              return;
            }

            switch (event.key) {
              case "`": {
                event.preventDefault();
                CustomEditorActions.toggleCodeBlock(editor);
                break;
              }

              case "b": {
                event.preventDefault();
                CustomEditorActions.toggleBoldMark(editor);

                break;
              }

              case "i": {
                event.preventDefault();
                CustomEditorActions.toggleItalicMark(editor);

                break;
              }

              case "e": {
                event.preventDefault();
                CustomEditorActions.toggleStrikeThroughMark(editor);

                break;
              }
              default: {
                // Default case does nothing

                break;
              }
            }
          }}
        />
      </Slate>
    </EditorContainer>
  );
};
const updateEditorExternal = (event, editor) => {
  console.log(sessionStorage.getItem("editorContent"), event.detail);
  // editor.insertText(event.detail);
  editor.apply({
    type: "remove_text",
    path: [0, 0],
    offset: 0,
    text: htmlToPlainText(sessionStorage.getItem("editorContent")),
  });
  editor.apply({
    type: "insert_text",
    path: [0, 0],
    offset: 0,
    text: event.detail + " ",
  });
  // debugger;
};

export default RichTextEditor;
export { updateEditorExternal };
